(function () {
  'use strict';

  const select = (selector, context = document) => context.querySelector(selector);
  const selectAll = (selector, context = document) => Array.from(context.querySelectorAll(selector));
  const config = window.Nerineri || {};

  const money = (cents) => {
    const numericValue = Number(cents);
    if (Number.isNaN(numericValue)) return String(cents || '');
    const format = config.moneyFormat || '€{{amount}}';
    return format.replace(/\{\{\s*(\w+?)\s*\}\}/g, (match, token) => {
      const value = numericValue / 100;
      if (token.includes('no_decimals')) return Math.round(value).toString();
      let output = value.toFixed(2);
      if (token.includes('comma_separator')) output = output.replace('.', ',');
      return output;
    });
  };

  const lockBody = () => { document.body.style.overflow = 'hidden'; };
  const unlockBody = () => {
    if (!select('.is-open.cart-drawer, .is-open.mobile-nav, .is-open.search-drawer, .is-open.filter-drawer, .is-open.quick-view')) {
      document.body.style.overflow = '';
    }
  };

  const escapeHtml = (value = '') => String(value).replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  })[character]);
  const isSizeOptionName = (name = '') => {
    const normalized = String(name || '').toLowerCase();
    return normalized.includes('size') || normalized.includes('dyd');
  };
  const orderOptionValues = (name, values = []) => {
    const source = Array.isArray(values) ? values : [];
    if (!isSizeOptionName(name)) return source;
    const preferred = ['XS', 'S', 'M', 'L', 'XL'];
    const used = new Set();
    const ordered = [];
    preferred.forEach((target) => {
      source.forEach((value, index) => {
        if (!used.has(index) && String(value).trim().toUpperCase() === target) {
          ordered.push(value);
          used.add(index);
        }
      });
    });
    source.forEach((value, index) => {
      if (!used.has(index)) ordered.push(value);
    });
    return ordered;
  };
  const setExpanded = (selector, expanded) => {
    selectAll(selector).forEach((element) => element.setAttribute('aria-expanded', String(expanded)));
  };
  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const CartDrawer = {
    el: null,
    trigger: null,
    init() {
      this.el = select('#CartDrawer');
      if (!this.el) return;
      document.addEventListener('click', async (event) => {
        const toggle = event.target.closest('[data-cart-toggle]');
        if (toggle) {
          event.preventDefault();
          await this.refresh();
          this.open(toggle);
        }
      });
      this.el.addEventListener('click', (event) => {
        if (event.target.closest('[data-cart-close]')) this.close();
      });
      this.bindLineEvents();
    },
    open(trigger = null) {
      if (!this.el) return;
      this.trigger = trigger || this.trigger;
      this.el.classList.add('is-open');
      this.el.setAttribute('aria-hidden', 'false');
      setExpanded('[data-cart-toggle]', true);
      lockBody();
      select('[data-cart-close]', this.el)?.focus();
    },
    close() {
      if (!this.el) return;
      const wasOpen = this.el.classList.contains('is-open');
      this.el.classList.remove('is-open');
      this.el.setAttribute('aria-hidden', 'true');
      setExpanded('[data-cart-toggle]', false);
      unlockBody();
      if (wasOpen) this.trigger?.focus();
    },
    async refresh() {
      try {
        const root = config.rootUrl || '/';
        const response = await fetch(`${root}?section_id=cart-drawer`);
        const text = await response.text();
        const documentFragment = new DOMParser().parseFromString(text, 'text/html');
        const fresh = documentFragment.querySelector('#CartDrawer .cart-drawer__panel');
        const current = select('.cart-drawer__panel', this.el);
        if (fresh && current) current.innerHTML = fresh.innerHTML;
        this.bindLineEvents();
        await this.updateCount();
      } catch (error) {
        console.error('Nepavyko atnaujinti krepšelio.', error);
      }
    },
    async updateCount() {
      const response = await fetch(`${config.cartUrl || '/cart'}.js`);
      const cart = await response.json();
      selectAll('[data-cart-count]').forEach((node) => {
        node.textContent = cart.item_count;
        node.style.display = cart.item_count > 0 ? 'flex' : 'none';
      });
    },
    bindLineEvents() {
      selectAll('[data-line-remove]', this.el).forEach((button) => {
        button.onclick = () => this.changeLine(button.dataset.lineKey, 0);
      });
      selectAll('[data-qty-down]', this.el).forEach((button) => {
        button.onclick = () => {
          const input = select('input', button.parentElement);
          this.changeLine(button.dataset.lineKey, Math.max(0, Number(input.value) - 1));
        };
      });
      selectAll('[data-qty-up]', this.el).forEach((button) => {
        button.onclick = () => {
          const input = select('input', button.parentElement);
          this.changeLine(button.dataset.lineKey, Number(input.value) + 1);
        };
      });
      selectAll('[data-upsell-add]', this.el).forEach((button) => {
        button.onclick = () => addToCart(button.dataset.variantId, 1, button);
      });
      const note = select('[data-cart-note]', this.el);
      if (note) {
        let timeout;
        note.oninput = () => {
          clearTimeout(timeout);
          timeout = setTimeout(() => {
            fetch(`${config.cartUpdateUrl || '/cart/update'}.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ note: note.value })
            });
          }, 500);
        };
      }
    },
    async changeLine(key, quantity) {
      await fetch(`${config.cartChangeUrl || '/cart/change'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      await this.refresh();
    }
  };

  async function addToCart(variantId, quantity = 1, button) {
    if (!variantId) return;
    const originalLabel = button?.textContent;
    if (button) {
      button.disabled = true;
      button.textContent = config.strings?.adding || 'Adding...';
    }
    try {
      const response = await fetch(`${config.cartAddUrl || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity }] })
      });
      if (!response.ok) throw new Error(config.strings?.cartError || 'Cart request failed.');
      await CartDrawer.refresh();
      CartDrawer.open();
    } catch (error) {
      console.error(error);
      if (button) button.textContent = config.strings?.retry || 'Try again';
      return;
    } finally {
      if (button) {
        button.disabled = false;
        window.setTimeout(() => { button.textContent = originalLabel || config.strings?.addToCart || 'Add to cart'; }, 700);
      }
    }
  }
  window.NerineriAddToCart = addToCart;

  const Product = {
    root: null,
    variants: [],
    options: {},
    slides: [],
    thumbs: [],
    currentIndex: 0,
    init() {
      this.root = select('[data-product]');
      if (!this.root || this.root.dataset.productInitialized === 'true') return;
      this.root.dataset.productInitialized = 'true';
      this.variants = JSON.parse(select('[data-variants-json]', this.root)?.textContent || '[]');

      this.initGallery();
      this.initOptions();

      const form = select('.product__form', this.root);
      form?.addEventListener('submit', (event) => {
        event.preventDefault();
        const variantId = select('[name="id"]', form)?.value;
        const quantity = Number(select('[name="quantity"]', form)?.value || 1);
        addToCart(variantId, quantity, select('[type="submit"]', form));
      });

      select('[data-product-qty-down]', this.root)?.addEventListener('click', () => this.changeQuantity(-1));
      select('[data-product-qty-up]', this.root)?.addEventListener('click', () => this.changeQuantity(1));
      this.initZoom();
      this.initStickyAdd();
      this.updateAvailability();
      this.updateVariant(false);
    },
    initGallery() {
      const gallery = select('[data-product-gallery]', this.root);
      if (!gallery) return;
      this.slides = selectAll('[data-product-slide]', gallery);
      this.thumbs = selectAll('[data-product-thumb]', gallery);
      this.currentIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));

      if (this.slides.length <= 1) {
        this.showMediaByIndex(this.currentIndex, false);
        return;
      }

      select('[data-product-prev]', gallery)?.addEventListener('click', () => this.showMediaByIndex(this.currentIndex - 1));
      select('[data-product-next]', gallery)?.addEventListener('click', () => this.showMediaByIndex(this.currentIndex + 1));
      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.showMediaById(thumb.dataset.mediaId)));

      const stage = select('[data-product-stage]', gallery);
      stage?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.showMediaByIndex(this.currentIndex - 1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.showMediaByIndex(this.currentIndex + 1);
        }
      });

      let startX = 0;
      let startY = 0;
      stage?.addEventListener('touchstart', (event) => {
        startX = event.touches[0]?.clientX || 0;
        startY = event.touches[0]?.clientY || 0;
      }, { passive: true });
      stage?.addEventListener('touchend', (event) => {
        const touch = event.changedTouches[0];
        if (!touch) return;
        const deltaX = touch.clientX - startX;
        const deltaY = touch.clientY - startY;
        if (Math.abs(deltaX) < 48 || Math.abs(deltaX) < Math.abs(deltaY)) return;
        this.showMediaByIndex(deltaX < 0 ? this.currentIndex + 1 : this.currentIndex - 1);
      }, { passive: true });

      this.showMediaByIndex(this.currentIndex, false);
    },
    showMediaById(mediaId) {
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index >= 0) this.showMediaByIndex(index);
    },
    showMediaByIndex(index, animate = true) {
      if (!this.slides.length) return;
      const nextIndex = (index + this.slides.length) % this.slides.length;
      this.currentIndex = nextIndex;
      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === nextIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', String(!isActive));
        if (!isActive) slide.querySelectorAll('video').forEach((video) => video.pause?.());
      });
      this.thumbs.forEach((thumb, thumbIndex) => {
        const isActive = thumbIndex === nextIndex;
        thumb.classList.toggle('is-active', isActive);
        if (isActive) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      const counter = select('[data-product-current]', this.root);
      if (counter) counter.textContent = String(nextIndex + 1);
      const stage = select('[data-product-stage]', this.root);
      if (animate && stage) stage.classList.add('is-changing');
      window.setTimeout(() => stage?.classList.remove('is-changing'), 260);
    },
    initOptions() {
      selectAll('[data-option]', this.root).forEach((input) => {
        if (input.checked) this.options[input.dataset.optionPosition] = input.value;
        input.addEventListener('change', () => {
          if (input.disabled) return;
          const position = input.dataset.optionPosition;
          this.options[position] = input.value;
          const group = input.closest('.option-values');
          selectAll('label', group).forEach((item) => item.classList.remove('is-selected'));
          input.closest('label')?.classList.add('is-selected');
          this.updateSelectedLabels();
          this.updateAvailability();
          this.updateVariant(true);
        });
      });
      this.updateSelectedLabels();
    },
    updateSelectedLabels() {
      Object.keys(this.options).forEach((position) => {
        const selected = select(`[data-option-selected="${position}"]`, this.root);
        const input = select(`[data-option-position="${position}"][value="${CSS.escape(this.options[position] || '')}"]`, this.root);
        if (selected) selected.textContent = input?.dataset.optionLabel || this.options[position] || '';
      });
    },
    findVariant() {
      return this.variants.find((item) =>
        Object.keys(this.options).every((position) => item[`option${position}`] === this.options[position])
      );
    },
    updateAvailability() {
      selectAll('[data-option-group]', this.root).forEach((group) => {
        const position = group.dataset.optionPosition;
        selectAll('[data-option]', group).forEach((input) => {
          const testOptions = { ...this.options, [position]: input.value };
          const available = this.variants.some((variant) =>
            Object.keys(testOptions).every((optionPosition) => variant[`option${optionPosition}`] === testOptions[optionPosition]) && variant.available
          );
          input.disabled = !available;
          input.closest('label')?.classList.toggle('is-unavailable', !available);
        });
      });
    },
    changeQuantity(change) {
      const input = select('[name="quantity"]', this.root);
      if (input) input.value = Math.max(1, Number(input.value || 1) + change);
    },
    updateVariant(updateUrl = true) {
      const variant = this.findVariant();
      const idField = select('[name="id"]', this.root);
      const price = select('[data-product-price]', this.root);
      const addButton = select('.product__form [type="submit"]', this.root);
      const stickyButton = select('[data-sticky-add]');
      if (!variant) {
        [addButton, stickyButton].forEach((button) => {
          if (!button) return;
          button.disabled = true;
          button.textContent = config.strings?.unavailable || 'Neprieinama';
        });
        return;
      }

      if (idField) idField.value = variant.id;
      if (price) {
        price.innerHTML = variant.compare_at_price > variant.price
          ? `<s>${money(variant.compare_at_price)}</s><span class="price--sale">${money(variant.price)}</span>`
          : money(variant.price);
      }
      [addButton, stickyButton].forEach((button) => {
        if (!button) return;
        button.disabled = !variant.available;
        button.textContent = variant.available
          ? (config.strings?.addToCart || 'Add to cart')
          : (config.strings?.soldOut || 'Sold out');
      });
      const stickyPrice = select('.sticky-atc__price');
      if (stickyPrice) stickyPrice.textContent = money(variant.price);
      const sku = select('[data-product-sku]', this.root);
      if (sku) {
        sku.textContent = variant.sku || '—';
        sku.closest('div')?.toggleAttribute('hidden', !variant.sku);
      }
      const mediaId = variant.featured_media?.id || variant.featured_image?.id;
      if (mediaId) this.showMediaById(mediaId);
      if (updateUrl && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    },
    initZoom() {
      const dialog = select('[data-product-zoom-dialog]');
      const image = select('[data-product-zoom-image]', dialog);
      if (!dialog || !image) return;
      selectAll('[data-product-zoom]', this.root).forEach((button) => {
        button.addEventListener('click', () => {
          image.src = button.dataset.zoomSrc;
          dialog.showModal();
        });
      });
      select('[data-product-zoom-close]', dialog)?.addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
    },
    initStickyAdd() {
      const bar = select('[data-sticky-atc]');
      const form = select('.product__form', this.root);
      if (!bar || !form || !('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
      });
      observer.observe(form);
      select('[data-sticky-add]', bar)?.addEventListener('click', () => {
        const variantId = select('[name="id"]', form)?.value;
        const quantity = Number(select('[name="quantity"]', form)?.value || 1);
        addToCart(variantId, quantity, select('[data-sticky-add]', bar));
      });
    }
  };

  const MobileNav = {
    el: null,
    trigger: null,
    init() {
      this.el = select('#MobileNav');
      if (!this.el) return;
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-mobile-nav-open]');
        if (trigger) this.open(trigger);
        if (event.target.closest('[data-mobile-nav-close]')) this.close();
      });
    },
    open(trigger = null) {
      if (!this.el) return;
      this.trigger = trigger || this.trigger;
      this.el.classList.add('is-open');
      this.el.setAttribute('aria-hidden', 'false');
      setExpanded('[data-mobile-nav-open]', true);
      lockBody();
      select('[data-mobile-nav-close]', this.el)?.focus();
    },
    close() {
      if (!this.el) return;
      const wasOpen = this.el.classList.contains('is-open');
      this.el.classList.remove('is-open');
      this.el.setAttribute('aria-hidden', 'true');
      setExpanded('[data-mobile-nav-open]', false);
      unlockBody();
      if (wasOpen) this.trigger?.focus();
    }
  };

  const SearchDrawer = {
    el: null,
    timer: null,
    trigger: null,
    init() {
      this.el = select('#SearchDrawer');
      if (!this.el) return;
      document.addEventListener('click', (event) => {
        if (event.target.closest('[data-search-open]')) {
          event.preventDefault();
          this.open(event.target.closest('[data-search-open]'));
        }
        if (event.target.closest('[data-search-close]')) this.close();
      });
      select('[data-search-input]', this.el)?.addEventListener('input', (event) => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.search(event.target.value.trim()), 220);
      });
    },
    open(trigger = null) {
      if (!this.el) return;
      this.trigger = trigger || this.trigger;
      MobileNav.close();
      this.el.classList.add('is-open');
      this.el.setAttribute('aria-hidden', 'false');
      setExpanded('[data-search-open]', true);
      lockBody();
      window.setTimeout(() => select('[data-search-input]', this.el)?.focus(), 350);
    },
    close() {
      if (!this.el) return;
      const wasOpen = this.el.classList.contains('is-open');
      this.el.classList.remove('is-open');
      this.el.setAttribute('aria-hidden', 'true');
      setExpanded('[data-search-open]', false);
      unlockBody();
      if (wasOpen) this.trigger?.focus();
    },
    async search(query) {
      const results = select('[data-search-results]', this.el);
      const suggestions = select('[data-search-suggestions]', this.el);
      if (!results) return;
      if (query.length < 2) {
        results.innerHTML = '';
        if (suggestions) suggestions.hidden = false;
        return;
      }
      if (suggestions) suggestions.hidden = true;
      results.innerHTML = '<p class="predictive-search__loading">' + escapeHtml(config.strings?.searchLoading || 'Searching...') + '</p>';
      try {
        const endpoint = config.predictiveSearchUrl || '/search/suggest';
        const response = await fetch(`${endpoint}.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=4`);
        if (!response.ok) throw new Error(config.strings?.searchError || 'Search request failed.');
        const payload = await response.json();
        const products = payload.resources?.results?.products || [];
        if (!products.length) {
          results.innerHTML = `
            <div class="predictive-search__empty">
              <h3>${escapeHtml(config.strings?.searchNoResults || 'No results found')}</h3>
              <p>${escapeHtml(config.strings?.searchNoResultsText || 'Try another search term.')}</p>
              <a class="link-arrow" href="${config.collectionsUrl || '/collections'}">${escapeHtml(config.strings?.searchViewCategories || 'View categories')} →</a>
            </div>`;
          return;
        }
        results.innerHTML = `
          <div class="predictive-search__grid">
            ${products.map((product) => {
              const imageUrl = typeof product.image === 'string' ? product.image : product.image?.url;
              return `
                <a class="predictive-product" href="${escapeHtml(product.url)}">
                  ${imageUrl ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(product.title)}" width="72" height="90">` : ''}
                  <span>
                    <h3>${escapeHtml(product.title)}</h3>
                    <p>${money(product.price)}</p>
                  </span>
                </a>`;
            }).join('')}
          </div>
          <a class="link-arrow predictive-search__all" href="${config.searchUrl || '/'}?q=${encodeURIComponent(query)}">${escapeHtml(config.strings?.searchViewAllResults || 'View all results')} →</a>`;
      } catch (error) {
        results.innerHTML = '<p class="predictive-search__empty">' + escapeHtml(config.strings?.searchError || 'Search is temporarily unavailable. Please try again.') + '</p>';
      }
    }
  };

  const QuickView = {
    el: null,
    data: null,
    selected: [],
    trigger: null,
    init() {
      this.el = select('#QuickView');
      if (!this.el) return;
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-quick-view]');
        if (trigger) {
          const card = trigger.closest('.product-card');
          const json = select('[data-quick-product]', card)?.textContent;
          if (json) this.open(JSON.parse(json), trigger);
        }
        if (event.target.closest('[data-quick-view-close]')) this.close();
      });
      select('[data-quick-view-form]', this.el)?.addEventListener('submit', (event) => {
        event.preventDefault();
        const variantId = select('[data-quick-view-variant]', this.el)?.value;
        const quantity = Number(select('[name="quantity"]', this.el)?.value || 1);
        addToCart(variantId, quantity, select('[data-quick-view-add]', this.el));
      });
      select('[data-quick-view-qty-down]', this.el)?.addEventListener('click', () => this.changeQuantity(-1));
      select('[data-quick-view-qty-up]', this.el)?.addEventListener('click', () => this.changeQuantity(1));
    },
    open(data, trigger = null) {
      if (!this.el) return;
      this.data = data;
      this.trigger = trigger || this.trigger;
      this.selected = (data.options || []).map((option) => {
        const values = orderOptionValues(option.name, option.values || []);
        return option.selected_value || values[0];
      });
      const image = select('[data-quick-view-image]', this.el);
      if (image && data.image) {
        image.src = data.image;
        image.alt = data.title;
        image.hidden = false;
      } else if (image) {
        image.hidden = true;
      }
      select('[data-quick-view-title]', this.el).textContent = data.title;
      select('[data-quick-view-link]', this.el).href = data.url;
      const options = select('[data-quick-view-options]', this.el);
      options.innerHTML = (data.options || []).map((option, index) => {
        const values = orderOptionValues(option.name, option.values || []);
        return `
        <div class="quick-view__option">
          <label for="QuickOption${index}">${escapeHtml(this.translateOption(option.name))}</label>
          <select id="QuickOption${index}" data-quick-view-option="${index}">
            ${values.map((value) => `<option value="${escapeHtml(value)}"${this.selected[index] === value ? ' selected' : ''}>${escapeHtml(this.translateOptionValue(option.name, value))}</option>`).join('')}
          </select>
        </div>`;
      }).join('');
      selectAll('[data-quick-view-option]', options).forEach((field) => {
        field.addEventListener('change', () => {
          this.selected[Number(field.dataset.quickViewOption)] = field.value;
          this.updateVariant();
        });
      });
      const quantity = select('[name="quantity"]', this.el);
      if (quantity) quantity.value = 1;
      this.updateVariant();
      this.el.classList.add('is-open');
      this.el.setAttribute('aria-hidden', 'false');
      lockBody();
      select('[data-quick-view-close]', this.el)?.focus();
    },
    translateOption(name) {
      const normalized = String(name || '').toLowerCase();
      if (normalized.includes('color') || normalized.includes('colour') || normalized.includes('spalv')) return config.strings?.color || 'Color';
      if (normalized.includes('size') || normalized.includes('dyd')) return config.strings?.size || 'Size';
      return name;
    },
    translateOptionValue(name, value) {
      const optionName = String(name || '').toLowerCase();
      if (!optionName.includes('color') && !optionName.includes('colour') && !optionName.includes('spalv')) return value;
      const normalized = String(value || '').toLowerCase();
      if (normalized.includes('ruda')) return 'Brown';
      if (normalized.includes('juoda')) return 'Black';
      if (normalized.includes('tamsiai mėlyna') || normalized.includes('tamsiai melyna') || normalized.includes('dark blue') || normalized.includes('navy')) return 'Navy blue';
      if (normalized.includes('chaki')) return 'Khaki';
      if (normalized.includes('smėlio') || normalized.includes('smelio')) return 'Sand';
      return value;
    },
    close() {
      if (!this.el) return;
      const wasOpen = this.el.classList.contains('is-open');
      this.el.classList.remove('is-open');
      this.el.setAttribute('aria-hidden', 'true');
      unlockBody();
      if (wasOpen) this.trigger?.focus();
    },
    updateVariant() {
      const variants = this.data?.variants || [];
      const variant = variants.find((item) => this.selected.every((value, index) => item[`option${index + 1}`] === value)) || variants[0];
      if (!variant) return;
      select('[data-quick-view-variant]', this.el).value = variant.id;
      select('[data-quick-view-price]', this.el).textContent = money(variant.price);
      const button = select('[data-quick-view-add]', this.el);
      button.disabled = !variant.available;
      button.textContent = variant.available ? (config.strings?.addToCart || 'Add to cart') : (config.strings?.soldOut || 'Sold out');
      if (variant.featured_image?.src) select('[data-quick-view-image]', this.el).src = variant.featured_image.src;
    },
    changeQuantity(change) {
      const input = select('[name="quantity"]', this.el);
      if (input) input.value = Math.max(1, Number(input.value || 1) + change);
    }
  };

  const FilterDrawer = {
    el: null,
    trigger: null,
    init() {
      this.el = select('[data-filter-drawer]');
      if (!this.el) return;
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-filter-open]');
        if (trigger) this.open(trigger);
        if (event.target.closest('[data-filter-close]')) this.close();
      });
    },
    open(trigger = null) {
      if (!this.el) return;
      this.trigger = trigger || this.trigger;
      this.el.classList.add('is-open');
      this.el.setAttribute('aria-hidden', 'false');
      lockBody();
      select('[data-filter-close]', this.el)?.focus();
    },
    close() {
      if (!this.el) return;
      const wasOpen = this.el.classList.contains('is-open');
      this.el.classList.remove('is-open');
      this.el.setAttribute('aria-hidden', 'true');
      unlockBody();
      if (wasOpen) this.trigger?.focus();
    }
  };

  function initQuickAdd() {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-card-add]');
      if (!button) return;
      addToCart(button.dataset.variantId, 1, button);
    });
    document.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-quick-add]');
      if (!form) return;
      event.preventDefault();
      addToCart(select('[name="id"]', form)?.value, 1, select('button', form));
    });
  }

  function initHeader() {
    const announcement = select('.announcement');
    const header = select('[data-site-header]');
    if (!header) return;
    const setHeight = () => {
      document.documentElement.style.setProperty('--announcement-height', `${announcement?.offsetHeight || 0}px`);
    };
    const update = () => header.classList.toggle('is-stuck', window.scrollY > 120);
    setHeight();
    update();
    window.addEventListener('resize', setHeight, { passive: true });
    window.addEventListener('scroll', update, { passive: true });
  }

  function initReveal() {
    const elements = selectAll('.reveal');
    if (!elements.length || !('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -4% 0px' });
    elements.forEach((element) => observer.observe(element));
  }

  function initNewsletter() {
    selectAll('[data-newsletter-form], .email-form').forEach((form) => {
      if (form.dataset.newsletterInitialized === 'true') return;
      form.dataset.newsletterInitialized = 'true';
      form.addEventListener('submit', () => {
        const button = select('[type="submit"]', form);
        if (button) button.textContent = 'SIUNČIAMA...';
      });
    });
  }

  function initAccordions() {
    selectAll('.accordion').forEach((details) => {
      if (details.dataset.accordionInitialized === 'true') return;
      details.dataset.accordionInitialized = 'true';
      const summary = select('summary', details);
      if (!summary) return;
      summary.setAttribute('aria-expanded', String(details.open));
      details.addEventListener('toggle', () => {
        summary.setAttribute('aria-expanded', String(details.open));
      });
    });
  }

  function initProductRails() {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-product-rail-prev], [data-product-rail-next]');
      if (!button) return;
      const section = button.closest('.featured-collection');
      const rail = select('[data-product-rail]', section);
      if (!rail) return;
      const direction = button.matches('[data-product-rail-prev]') ? -1 : 1;
      const card = rail.querySelector('.product-card');
      const distance = card ? card.getBoundingClientRect().width + 18 : rail.clientWidth * 0.78;
      rail.scrollBy({ left: distance * direction, behavior: 'smooth' });
    });
  }

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      if (QuickView.el?.classList.contains('is-open')) QuickView.close();
      else if (SearchDrawer.el?.classList.contains('is-open')) SearchDrawer.close();
      else if (FilterDrawer.el?.classList.contains('is-open')) FilterDrawer.close();
      else if (MobileNav.el?.classList.contains('is-open')) MobileNav.close();
      else if (CartDrawer.el?.classList.contains('is-open')) CartDrawer.close();
      return;
    }
    if (event.key !== 'Tab') return;
    const dialog = select('.quick-view.is-open [role="dialog"], .search-drawer.is-open [role="dialog"], .filter-drawer.is-open .filter-drawer__panel, .mobile-nav.is-open [role="dialog"], .cart-drawer.is-open [role="dialog"]');
    if (!dialog) return;
    const focusable = selectAll(focusableSelector, dialog).filter((element) => element.offsetParent !== null);
    if (focusable.length < 2) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener('DOMContentLoaded', () => {
    CartDrawer.init();
    Product.init();
    MobileNav.init();
    SearchDrawer.init();
    FilterDrawer.init();
    QuickView.init();
    initQuickAdd();
    initHeader();
    initReveal();
    initNewsletter();
    initAccordions();
    initProductRails();
  });
})();
